import { useEffect, useState } from 'react'
import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import { Input } from './ui/input'
import { Label } from './ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { LUDOLIO_PRIVACY_POLICY_URL, LUDOLIO_TERMS_OF_USE_URL } from '../config/constants'

export interface OrgNoticeInfo {
  id: string
  title: string
  url: string
}

export interface OnboardingSubmitValues {
  firstName: string
  lastName: string
  marketingConsent: boolean
  orgNoticeId: string | null
}

export interface OnboardingDialogProps {
  open: boolean
  initialFirstName?: string
  initialLastName?: string
  orgName?: string | null
  /** The organization's custom notice; null/undefined when the org has none */
  notice?: OrgNoticeInfo | null
  submitting?: boolean
  error?: string
  loadError?: string
  onSubmit: (values: OnboardingSubmitValues) => void
  onRetry?: () => void
  /** Clears the session and returns to login when the user wants a different account */
  onUseDifferentAccount?: () => void
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium underline underline-offset-2 text-[hsl(var(--color-primary-text))] transition-colors hover:text-[hsl(var(--color-primary-text-hover))]"
    >
      {children}
    </a>
  )
}

/**
 * Blocking first-login onboarding dialog: name confirmation, privacy/terms
 * acknowledgement, optional per-organization notice, and marketing opt-in.
 * Purely presentational; the host app wires submission to the API.
 */
export function OnboardingDialog({
  open,
  initialFirstName = '',
  initialLastName = '',
  orgName,
  notice,
  submitting = false,
  error = '',
  loadError = '',
  onSubmit,
  onRetry,
  onUseDifferentAccount,
}: OnboardingDialogProps) {
  const [firstName, setFirstName] = useState(initialFirstName)
  const [lastName, setLastName] = useState(initialLastName)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [noticeAccepted, setNoticeAccepted] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)

  // Reset the form each time the dialog opens (e.g. a different account logs in)
  useEffect(() => {
    if (open) {
      setFirstName(initialFirstName)
      setLastName(initialLastName)
      setPrivacyAccepted(false)
      setNoticeAccepted(false)
      setMarketingConsent(false)
    }
  }, [open, initialFirstName, initialLastName, notice?.id])

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    privacyAccepted &&
    (!notice || noticeAccepted) &&
    !loadError &&
    !submitting

  const handleSubmit = () => {
    if (!canSubmit) return
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      marketingConsent,
      orgNoticeId: notice && noticeAccepted ? notice.id : null,
    })
  }

  return (
    <Dialog open={open}>
      <DialogContent
        className="[&>button.absolute]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Welcome to Ludolio</DialogTitle>
          <DialogDescription>
            Confirm your details below to finish setting up your account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="onboardingFirstName">First name</Label>
              <Input
                id="onboardingFirstName"
                type="text"
                placeholder="First name"
                autoComplete="given-name"
                data-protonpass-ignore="true"
                data-1p-ignore
                data-lpignore="true"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={submitting || Boolean(loadError)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="onboardingLastName">Last name</Label>
              <Input
                id="onboardingLastName"
                type="text"
                placeholder="Last name"
                autoComplete="family-name"
                data-protonpass-ignore="true"
                data-1p-ignore
                data-lpignore="true"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={submitting || Boolean(loadError)}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <Checkbox
                id="onboardingPrivacy"
                checked={privacyAccepted}
                onCheckedChange={(checked) => setPrivacyAccepted(checked === true)}
                disabled={submitting || Boolean(loadError)}
              />
              <Label
                htmlFor="onboardingPrivacy"
                className="text-xs leading-relaxed font-normal cursor-pointer"
              >
                I have read and agree to the Ludolio{' '}
                <ExternalLink href={LUDOLIO_PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink> and{' '}
                <ExternalLink href={LUDOLIO_TERMS_OF_USE_URL}>Terms of Use</ExternalLink>.
              </Label>
            </div>

            {notice && (
              <div className="flex items-start gap-2">
                <Checkbox
                  id="onboardingOrgNotice"
                  checked={noticeAccepted}
                  onCheckedChange={(checked) => setNoticeAccepted(checked === true)}
                  disabled={submitting || Boolean(loadError)}
                />
                <Label
                  htmlFor="onboardingOrgNotice"
                  className="text-xs leading-relaxed font-normal cursor-pointer"
                >
                  I have read the{' '}
                  <ExternalLink href={notice.url}>
                    {orgName ? `${orgName} ${notice.title}` : notice.title}
                  </ExternalLink>
                </Label>
              </div>
            )}

            <div className="flex items-start gap-2">
              <Checkbox
                id="onboardingMarketing"
                checked={marketingConsent}
                onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                disabled={submitting || Boolean(loadError)}
              />
              <Label
                htmlFor="onboardingMarketing"
                className="text-xs leading-relaxed font-normal cursor-pointer text-[hsl(var(--color-muted-foreground))]"
              >
                Send me updates about new games and features. (Optional)
              </Label>
            </div>
          </div>

          {loadError && (
            <div className="space-y-3 rounded-md border border-[hsl(var(--color-destructive))] bg-[hsl(var(--color-destructive))]/10 px-4 py-3 text-sm text-[hsl(var(--color-destructive))]">
              <p>{loadError}</p>
              {onRetry && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  disabled={submitting}
                >
                  Try again
                </Button>
              )}
            </div>
          )}

          {error && !loadError && (
            <div className="rounded-md border border-[hsl(var(--color-destructive))] bg-[hsl(var(--color-destructive))]/10 px-4 py-3 text-sm text-[hsl(var(--color-destructive))]">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
            {submitting ? 'Saving...' : 'Continue'}
          </Button>
          {onUseDifferentAccount && (
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={onUseDifferentAccount}
              disabled={submitting}
            >
              Use a different account
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
